import type { Frame, Project, Space } from "@/api/spaces";
import { calculateConversationFocusBoxes } from "@/lib/conversation-focus-layout-geometry";
import { focusConversationFrameLayout } from "@/lib/conversation-focus-layout";
import { exitPresentationMode } from "@/lib/presentation-mode";
import { currentFocusSurfaceBox } from "@/lib/focus/focus-surface";
import { MAX_FOCUS_FRAMES } from "@/lib/focus-window-layout";
import { useWorkspaceLoadStore } from "@/stores/workspace-load-store";
import { useCanvasStore } from "@/stores/canvas-store";
import { usePresentationStore } from "@/stores/presentation-store";
import { useSelectionStore } from "@/stores/selection-store";
import { useDesktopMode } from "@/stores/desktop-mode-store";
import { useProjectStore } from "@/stores/project-store";
import { isTransientConversationFrame } from "@/lib/transient-conversation-frame";
import { useWorkspaceConversationOverlay } from "@/stores/workspace-conversation-overlay-store";
import { workspaceOpenOnOtherDisplay } from "@/desktop/workspace-holder";
import { setFrameHidden } from "@/lib/frame-visibility";
import {
  applyFramePatch,
  discardFrames,
  ingestFrame,
  ingestFrames,
} from "@/lib/frame-ingest";
import { useFramesStore } from "@/stores/frames-store";
import {
  isFrameHiddenByMetadata,
  selectOrderedFrames,
} from "@/stores/frames-selectors";
import { frameNodeHidden } from "@/stores/canvas-frame-geometry";

let conversationFrameRevealGeneration = 0;

export interface ConversationFrameRevealResult {
  frame: Frame;
  project: Project;
  space: Space;
  created: boolean;
  transient?: boolean;
}

export function conversationFrameRevealToken(): number {
  return conversationFrameRevealGeneration;
}

export function isConversationFrameRevealCurrent(token: number): boolean {
  return token === conversationFrameRevealGeneration;
}

export function cancelPendingConversationFrameReveals(): void {
  conversationFrameRevealGeneration += 1;
}

function canContinue(shouldContinue?: () => boolean): boolean {
  return shouldContinue?.() ?? true;
}

function conversationIdFromFrame(frame: Frame): string | null {
  const value = frame.metadata?.conversationId;
  return typeof value === "string" && value.length > 0 ? value : null;
}

function focusedDuplicateFrameId(frame: Frame): string | null {
  const conversationId = conversationIdFromFrame(frame);
  if (!conversationId) return null;
  const { presentation } = usePresentationStore.getState();
  if (presentation?.variant !== "focus") return null;
  const { framesById } = useFramesStore.getState();
  return (
    presentation.frameIds.find((id) => {
      const candidate = framesById[id];
      return (
        candidate?.frameType === "conversation" &&
        candidate.metadata.conversationId === conversationId
      );
    }) ?? null
  );
}

function removeLoadedConversationDuplicates(frame: Frame): void {
  const conversationId = conversationIdFromFrame(frame);
  if (!conversationId) return;
  const { framesById, frameOrder } = useFramesStore.getState();
  discardFrames(
    frameOrder.filter((id) => {
      const candidate = framesById[id];
      return (
        id !== frame.id &&
        candidate?.frameType === "conversation" &&
        candidate.metadata.conversationId === conversationId
      );
    }),
  );
}

function frameAtConversationFocusPosition(frame: Frame): Frame {
  if (!isTransientConversationFrame(frame)) return frame;
  const { presentation } = usePresentationStore.getState();
  const { framesById } = useFramesStore.getState();
  const focusedConversationIds =
    presentation?.variant === "focus"
      ? presentation.frameIds.filter(
          (id) => framesById[id]?.frameType === "conversation",
        )
      : [];
  const nextIds = focusedConversationIds.includes(frame.id)
    ? focusedConversationIds
    : [...focusedConversationIds, frame.id].slice(-MAX_FOCUS_FRAMES);
  const targetIndex = Math.max(0, nextIds.indexOf(frame.id));
  const box = calculateConversationFocusBoxes(
    currentFocusSurfaceBox(),
    nextIds.length,
  )[targetIndex];
  if (!box) return frame;
  return {
    ...frame,
    position_x: box.x,
    position_y: box.y,
    width: box.width,
    height: box.height,
  };
}

function shouldReplaceCanvas(spaceId: string): boolean {
  const { presentation } = usePresentationStore.getState();
  const frames = selectOrderedFrames(useFramesStore.getState());
  if (frames.length === 0) return true;
  const targetSpaceFrameIds = new Set(
    frames
      .filter((frame) => frame.spaceId === spaceId)
      .map((frame) => frame.frameId),
  );
  if (
    presentation?.variant === "focus" &&
    presentation.frameIds.some((id) => targetSpaceFrameIds.has(id))
  ) {
    return false;
  }
  return frames.some((frame) => frame.spaceId !== spaceId);
}

function refocusAfterNativeExpansionSettles(
  frameId: string,
  shouldContinue?: () => boolean,
): void {
  const desktop = useDesktopMode.getState();
  if (desktop.mode !== "denk") return;
  const presentation = usePresentationStore.getState().presentation;
  if (
    presentation?.variant !== "focus" ||
    !presentation.frameIds.includes(frameId)
  ) {
    return;
  }
  const expectedFrameIds = presentation.frameIds.join("\0");
  let timeoutId: number | null = null;
  let unsubscribe = () => {};
  let settled = false;
  const canRefocus = () => {
    if (!canContinue(shouldContinue)) return false;
    const desktopState = useDesktopMode.getState();
    if (desktopState.mode !== "denk") return false;
    const state = useCanvasStore.getState();
    const current = usePresentationStore.getState().presentation;
    const frame = useFramesStore.getState().framesById[frameId];
    return (
      current?.variant === "focus" &&
      current.frameIds.join("\0") === expectedFrameIds &&
      current.frameIds[current.activeIndex] === frameId &&
      useSelectionStore.getState().selectedFrameId === frameId &&
      !!frame &&
      !frameNodeHidden(frame, state.focusHiddenFrameIds)
    );
  };
  const focusAfterFrame = () => {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        if (canRefocus()) focusConversationFrameLayout(frameId);
      });
    });
  };
  if (!desktop.nativeGeometryTransitioning) {
    focusAfterFrame();
    window.setTimeout(focusAfterFrame, 250);
    return;
  }
  const finish = () => {
    if (settled) return;
    settled = true;
    unsubscribe();
    if (timeoutId !== null) window.clearTimeout(timeoutId);
    focusAfterFrame();
  };
  unsubscribe = useDesktopMode.subscribe((state) => {
    if (!state.nativeGeometryTransitioning) finish();
  });
  timeoutId = window.setTimeout(finish, 1_500);
}

function revealAndFocusFrame(
  frame: Frame,
  options: { replaceCanvas?: boolean; shouldContinue?: () => boolean } = {},
): void {
  const replaceCanvas = options.replaceCanvas ?? false;
  const duplicateFrameId = focusedDuplicateFrameId(frame);
  if (
    replaceCanvas ||
    (duplicateFrameId !== null && duplicateFrameId !== frame.id)
  ) {
    exitPresentationMode();
  }
  const state = useCanvasStore.getState();
  const positionedFrame = frameAtConversationFocusPosition(frame);
  if (replaceCanvas) {
    ingestFrames([positionedFrame]);
    state.loadConnections([]);
  } else if (useFramesStore.getState().framesById[frame.id]) {
    const updates: Partial<Frame> = { ...frame };
    if (isTransientConversationFrame(frame)) {
      delete updates.position_x;
      delete updates.position_y;
      delete updates.width;
      delete updates.height;
      const existing = state.nodes.find((node) => node.id === frame.id);
      if (existing?.position.x === 0 && existing.position.y === 0) {
        updates.position_x = positionedFrame.position_x;
        updates.position_y = positionedFrame.position_y;
        updates.width = positionedFrame.width;
        updates.height = positionedFrame.height;
      }
    }
    applyFramePatch(frame.id, updates, { origin: "server" });
  } else {
    ingestFrame(positionedFrame);
  }
  removeLoadedConversationDuplicates(frame);
  const revealed = useFramesStore.getState().framesById[frame.id];
  useCanvasStore.getState().selectFrame(frame.id);
  focusConversationFrameLayout(frame.id);
  if (isFrameHiddenByMetadata(revealed)) {
    setFrameHidden(frame.id, false);
  }
  const desktop = useDesktopMode.getState();
  if (desktop.mode === "compact") {
    const presentation = usePresentationStore.getState().presentation;
    desktop.closeWorkspace(
      presentation?.variant === "focus" ? presentation.frameIds : [frame.id],
    );
  }
  refocusAfterNativeExpansionSettles(frame.id, options.shouldContinue);
  if (useWorkspaceLoadStore.getState().isLoadingCanvas) {
    useWorkspaceLoadStore.getState().setIsLoadingCanvas(false);
  }
}

export function revealConversationFrameResult(
  result: ConversationFrameRevealResult,
  shouldContinue?: () => boolean,
): void {
  let deliveredToOverlay = false;
  let stopHydrationReveal = () => {};
  const shouldUseOverlay = () => {
    const { overlay } = useWorkspaceConversationOverlay.getState();
    return (
      useDesktopMode.getState().mode === "denk" ||
      // The canvas is mirrored: a Focus here would land on the other
      // display's Workspace, which exits it and drops this frame.
      workspaceOpenOnOtherDisplay() ||
      (overlay?.project.id === result.project.id &&
        overlay.space.id === result.space.id)
    );
  };
  const reveal = ({ replaceCanvas = false } = {}) => {
    if (deliveredToOverlay || !canContinue(shouldContinue)) return;
    const projectState = useProjectStore.getState();
    if (
      projectState.currentProject?.id !== result.project.id ||
      projectState.currentSpace?.id !== result.space.id
    ) {
      return;
    }
    if (shouldUseOverlay()) {
      deliveredToOverlay = true;
      stopHydrationReveal();
      useWorkspaceConversationOverlay.getState().open({
        frame: result.frame,
        project: result.project,
        space: result.space,
      });
      return;
    }
    revealAndFocusFrame(result.frame, { replaceCanvas, shouldContinue });
  };
  if (shouldUseOverlay()) {
    reveal();
    return;
  }
  const loadState = useWorkspaceLoadStore.getState();
  const targetFramesApplied = loadState.framesLoadedSpaceId === result.space.id;
  const replaceCanvas =
    !targetFramesApplied && shouldReplaceCanvas(result.space.id);
  window.setTimeout(() => reveal({ replaceCanvas }), 0);
  if (targetFramesApplied) return;
  let done = false;
  const unsubscribe = useWorkspaceLoadStore.subscribe((state) => {
    if (done || state.framesLoadedSpaceId !== result.space.id) return;
    stopHydrationReveal();
    reveal();
  });
  const timeout = window.setTimeout(() => stopHydrationReveal(), 4_000);
  stopHydrationReveal = () => {
    done = true;
    unsubscribe();
    window.clearTimeout(timeout);
  };
}
